import { useState, useEffect } from "react";
import { resolveThemeIdFromClasses, getTheme } from "../../theme/themes";

export function useIsDark() {
  // Derive from the theme registry so every dark palette (jungle, tokyo-night,
  // …) is detected — not just the two hardcoded classes it used to check.
  const check = () => getTheme(resolveThemeIdFromClasses(document.body.classList)).isDark;
  const [isDark, setIsDark] = useState(check);
  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(check()));
    observer.observe(document.body, { attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

/**
 * Active theme id, reactive to <body> class changes. Charts key off this so
 * they remount and re-read their CSS-variable colors on ANY theme switch
 * (dark→jungle, light→sunrise, →tokyo-night), which `isDark` alone misses.
 */
export function useThemeId() {
  const check = () => resolveThemeIdFromClasses(document.body.classList);
  const [themeId, setThemeId] = useState(check);
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeId(check()));
    observer.observe(document.body, { attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return themeId;
}

interface CardSectionHeaderProps {
  label: string;
  title?: string;
  isDark: boolean;
  rightContent?: React.ReactNode;
  compact?: boolean;
  inline?: boolean;
}

// Pro Terminal module header: "● LABEL" — accent dot + mono uppercase label,
// hairline bottom border. Used by every widget card → the harmony backbone.
const labelClass =
  "font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground";

function ModuleLabel({ label, title }: { label: string; title?: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
      <p className={`${labelClass} truncate`}>{label}</p>
      {title && (
        <>
          <span className="text-muted-foreground/40">·</span>
          <p className={`${labelClass} truncate`}>{title}</p>
        </>
      )}
    </div>
  );
}

export function CardSectionHeader({ label, title, isDark: _isDark, rightContent, compact, inline }: CardSectionHeaderProps) {
  // `inline` and the default both now render the same Pro Terminal module bar.
  // (The old non-inline "big title" mode is folded into the module label so
  // every widget shares one header anatomy.)
  void inline;
  return (
    <div
      className={`flex items-center justify-between gap-2 border-b border-border px-4 ${
        compact ? "py-2" : "py-2.5"
      }`}
    >
      <ModuleLabel label={label} title={title} />
      {rightContent && <div className="shrink-0">{rightContent}</div>}
    </div>
  );
}
